import React, { useEffect, useState } from "react";
import { Copy, Check, X, Tag } from "lucide-react";
import { toast } from "sonner";
import WhatsAppIcon from "./WhatsAppIcon";
import { WHATSAPP_URL } from "../lib/constants";

const CODE = "TURISTAR";
const DISMISS_KEY = "orion_sticky_coupon_dismissed";

/**
 * Sticky bottom bar for MOBILE only. Shows the TURISTAR coupon + a WhatsApp CTA.
 * Only appears after the reader scrolls a bit (so it doesn't interrupt the hero).
 * User can dismiss it — dismissal is persisted in localStorage for 24h.
 */
export default function StickyCoupon() {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Respect dismissal (24h window)
    try {
      const dismissedAt = Number(window.localStorage.getItem(DISMISS_KEY) || 0);
      if (dismissedAt && Date.now() - dismissedAt < 24 * 60 * 60 * 1000) return;
    } catch {}

    const onScroll = () => {
      if (window.scrollY > 400) {
        setVisible(true);
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    // If user is already past the threshold (SPA route change), show immediately
    if (window.scrollY > 400) setVisible(true);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(CODE);
      setCopied(true);
      toast.success("Cupom copiado!", {
        description: `${CODE} — 10% OFF na sua reserva`,
        duration: 2600,
      });
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Não foi possível copiar. Tente selecionar manualmente.");
    }
  };

  const dismiss = () => {
    setVisible(false);
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {}
  };

  if (!visible) return null;

  return (
    <div
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-[#1c0a25] via-[#2C1139] to-[#1c0a25] text-white border-t-2 border-orion-orange shadow-[0_-8px_24px_rgba(0,0,0,0.35)]"
      role="complementary"
      aria-label="Cupom TURISTAR — 10% off"
      data-testid="sticky-coupon"
    >
      <div className="flex items-center gap-2 px-3 py-2.5">
        <div className="flex items-center gap-1.5 text-orion-gold shrink-0">
          <Tag size={12} />
          <span className="text-[9px] uppercase tracking-[0.28em] font-semibold">10% OFF</span>
        </div>
        <button
          type="button"
          onClick={copy}
          className="flex-1 inline-flex items-center justify-center gap-1.5 border border-dashed border-orion-orange/70 bg-white/5 hover:bg-white/10 transition-colors px-2 py-1.5 font-mono tracking-widest text-[13px]"
          data-testid="sticky-coupon-copy"
          aria-label="Copiar cupom TURISTAR"
        >
          <span className="text-orion-orange font-semibold">{CODE}</span>
          {copied ? <Check size={12} className="text-emerald-300" /> : <Copy size={12} className="text-white/60" />}
        </button>
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 inline-flex items-center gap-1.5 bg-[#25D366] text-white px-3 py-2 text-[10px] uppercase tracking-widest hover:bg-[#1EBE57] transition-colors font-semibold"
          data-testid="sticky-coupon-whatsapp"
          aria-label="Falar no WhatsApp"
        >
          <WhatsAppIcon size={12} color="#ffffff" /> Reservar
        </a>
        <button
          type="button"
          onClick={dismiss}
          className="shrink-0 p-1 text-white/50 hover:text-white transition-colors"
          data-testid="sticky-coupon-close"
          aria-label="Fechar barra do cupom"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
